/**
 * CommitmentDetail (C-1f) — single post-approval commitment: metadata,
 * lifecycle FSM actions, linked registration, history. Route: /regulatory/commitments/$id
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import styles from './Regulatory.module.css';
import {
  useCommitmentDetailQuery, useCommitmentTransition,
  COMMITMENT_FSM, COMMITMENT_STATUS_LABEL, COMMITMENT_TYPE_LABEL,
} from './useRegulatory';

function fmtDT(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
}
function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function statusClass(s: string) {
  if (s === 'FULFILLED') return styles.stageDone;
  if (s === 'CANCELLED') return styles.stageWarn;
  return styles.stageActive;
}

export function CommitmentDetail({ commitmentId }: { commitmentId: string }) {
  const q = useCommitmentDetailQuery(commitmentId);
  const transition = useCommitmentTransition(commitmentId);
  const [msg, setMsg] = useState<string | null>(null);

  if (q.isLoading) return <main className={styles.page}><div className={styles.empty}>Loading commitment…</div></main>;
  if (q.isError || !q.data) return <main className={styles.page}><div className={styles.empty}>Commitment not found. <Link to="/regulatory/commitments">Back to commitments</Link></div></main>;
  const c = q.data;
  const next = COMMITMENT_FSM[c.status] ?? [];

  async function doTransition(to: string) {
    setMsg(null);
    try { await transition.mutateAsync({ toStatus: to }); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Transition failed.'); }
  }

  return (
    <main className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/regulatory/commitments" className={styles.crumb}>Commitments</Link>
        <span className={styles.crumbSep} aria-hidden>/</span>
        <span>{c.commitmentNumber}</span>
      </nav>

      <header className={styles.detailHead}>
        <div>
          <h1 className={styles.detailTitle}><span className={styles.mono}>{c.commitmentNumber}</span> · {c.title}</h1>
          <div className={styles.subline}>
            <span className={`${styles.pill} ${statusClass(c.status)}`}>{COMMITMENT_STATUS_LABEL[c.status] ?? c.status}</span>
            <span className={styles.pill}>{COMMITMENT_TYPE_LABEL[c.commitmentType] ?? c.commitmentType}</span>
            <span className={styles.pill}>{c.authority}</span>
          </div>
        </div>
        <div className={styles.actions}>
          {next.length === 0 ? <span className={styles.terminal}>Terminal state</span> : next.map((to) => (
            <button key={to} type="button" className={`${styles.btn} ${to === 'CANCELLED' ? styles.btnNeutral : styles.btnPrimary}`}
              onClick={() => void doTransition(to)} disabled={transition.isPending}>→ {COMMITMENT_STATUS_LABEL[to] ?? to}</button>
          ))}
        </div>
      </header>
      {msg && <div className={styles.err} role="alert">{msg}</div>}

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Commitment detail</h2>
        <dl className={styles.metaGrid}>
          <div><dt>Type</dt><dd>{COMMITMENT_TYPE_LABEL[c.commitmentType] ?? c.commitmentType}</dd></div>
          <div><dt>Authority</dt><dd>{c.authority}</dd></div>
          <div><dt>Linked registration</dt><dd>{c.registrationId
            ? <Link to="/regulatory/registrations/$id" params={{ id: c.registrationId }}>{c.registrationCode ?? c.registrationId}</Link>
            : '—'}</dd></div>
          <div><dt>Linked product</dt><dd>{c.productName ?? '—'}</dd></div>
          <div><dt>Owner</dt><dd>{c.ownerName ?? '—'}</dd></div>
          <div><dt>Due date</dt><dd>{fmtDate(c.dueDate)}</dd></div>
          <div><dt>Submitted</dt><dd>{fmtDate(c.submittedDate)}</dd></div>
          <div><dt>Fulfilled</dt><dd>{fmtDate(c.fulfilledDate)}</dd></div>
        </dl>
        {c.description && <div className={styles.descBlock}><strong>Description</strong><p>{c.description}</p></div>}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>History</h2>
        <ol className={styles.timeline}>
          {(c.history ?? []).map((h, i) => (
            <li key={i} className={styles.tlRow}>
              <span className={styles.tlWhen}>{fmtDT(h.at_utc)}</span>
              <span className={styles.tlWhat}>
                {h.event_kind === 'COMMITMENT_TRANSITION' && h.from_status
                  ? `${COMMITMENT_STATUS_LABEL[h.from_status] ?? h.from_status} → ${COMMITMENT_STATUS_LABEL[h.to_status ?? ''] ?? h.to_status}`
                  : h.event_kind.replaceAll('_', ' ').toLowerCase()}
                {h.note ? <span className={styles.tlNote}> — {h.note}</span> : null}
              </span>
              {h.actor_name && <span className={styles.sub}>{h.actor_name}</span>}
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}
